import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  ServiceStatusResponse,
  NextServiceStatusesResponse,
  AvailableStatusesForServiceTypes,
  StatusChangeData,
  ServiceObject,
} from '../@types/service.type';
import {
  ServiceStatusChangeRequest,
  getNextServiceStatuses,
  saveServiceStatus,
  getServiceCancelOrReopenStatuses,
} from '../api/services';
import {
  createServiceStatus,
  getServiceStatusByCode,
  getAllServiceStatus,
  updateServiceStatus,
  getPossibleStatusesByServiceTypes,
} from '../api/serviceStatuses';
import { PartActionKey } from '../configs/partAction';
import {
  AvailableStatusKey,
  CancelReOpenStatusKey,
  NextServiceStatusKey,
  ServiceStatusCodeKey,
  ServiceStatusKey,
} from '../configs/service-status';
import { ServiceTypeKey, ServiceTypeStatusOptionKey } from '../configs/service-type';
import {
  ServiceLogAllKey,
  CheckInKey,
  GetServiceKey,
  ServiceAttendanceKey,
} from '../configs/services';

export const useGetServiceStatuses = (): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceStatusKey],
    queryFn: () => getAllServiceStatus(),
  });
};

export const useGetNextServicesStatuses = (
  code: string,
  serviceType: string,
  serviceId: number
): UseQueryResult<NextServiceStatusesResponse> => {
  // Key on every input the BFF response depends on. The endpoint is
  // /possibleNextserviceStatuses/:code/:serviceType?serviceId=:serviceId and
  // returns different lists per service type (Install vs Repair vs Workshop)
  // and per service (which contributes upstream allowed-status config). Keying
  // on code alone leaks one job's options into another that shares the same
  // current status code.
  return useQuery({
    queryKey: [NextServiceStatusKey, code, serviceType, serviceId],
    queryFn: () => getNextServiceStatuses(code, serviceType, serviceId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: false,
  });
};

export const useGetServicesStatuses = (): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceStatusKey],
    queryFn: () => getAllServiceStatus(),
    enabled: false,
  });
};

export const useGetServiceStatusByCode = (code: string): UseQueryResult<ServiceStatusResponse> => {
  return useQuery({
    queryKey: [ServiceStatusKey, code],
    queryFn: () => getServiceStatusByCode(code),
    enabled: false,
  });
};

export const useGetServiceStatusByShortName = (
  code: string
): UseQueryResult<ServiceStatusResponse> => {
  return useQuery({
    queryKey: [ServiceStatusCodeKey, code],
    queryFn: () => getServiceStatusByCode(code),
    retry: false,
    enabled: false,
  });
};

export const useCreateServiceStatus = (
  code: string
): UseMutationResult<ServiceStatusResponse, Error, ServiceStatusResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceStatusResponse) => {
      return await createServiceStatus(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey, code],
      });
    },
  });
};

export const useUpdateServiceStatus = (
  code: string
): UseMutationResult<ServiceStatusResponse, Error, ServiceStatusResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceStatusResponse) => {
      return await updateServiceStatus(data.code, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceStatusKey, code],
      });
    },
  });
};

export const useSaveServiceStatus = (
  id: number
): UseMutationResult<ServiceObject, Error, Record<string, unknown>> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return await saveServiceStatus(id, data as unknown as ServiceStatusChangeRequest);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, id],
      });
      // Attendance metrics (History/Attendances tab + admin summary) derive from
      // check-in records, so refresh them after any check-in/checkout commit.
      await queryClient.invalidateQueries({
        queryKey: [ServiceAttendanceKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id],
      });
      // Invalidate next statuses since current status changed
      await queryClient.invalidateQueries({
        queryKey: [NextServiceStatusKey],
      });
    },
  });
};

export const useGetPossibleStatusesByServiceTypes = (
  data: StatusChangeData
): UseQueryResult<AvailableStatusesForServiceTypes> => {
  return useQuery({
    queryKey: [AvailableStatusKey, data?.serviceTypes],
    queryFn: () => getPossibleStatusesByServiceTypes(data.code, data.serviceTypes),
    enabled: false,
  });
};

export const useGetServiceCancelOrReopenStatuses = (
  option: string,
  statusCode: string,
  serviceType: string,
  serviceId: number
): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [CancelReOpenStatusKey, option, statusCode, serviceType, serviceId],
    queryFn: () => getServiceCancelOrReopenStatuses(option, statusCode, serviceType, serviceId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: false,
  });
};

export const useGetAllServiceStatus = (): UseQueryResult<ServiceStatusResponse[]> => {
  return useQuery({
    queryKey: [ServiceTypeStatusOptionKey],
    queryFn: () => getAllServiceStatus(),
  });
};
